import * as React from "react";
import { styled } from "@mui/system";
import { Colors } from "../utils/colors";
import { ThemeStyle, useTheme } from "../hooks/useTheme";
import { ReactNode, useState } from "react";
import { ThemeType } from "@markit/common.types";

type NumberInputProps = {
  min: number;
  max: number;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  disabled?: boolean;
  containerStyles?: React.CSSProperties;
  prefix?: ReactNode;
  isError?: boolean; // Any additional conditions that should cause an error
  altErrorMessage?: string;
  suppressErrorMessage?: boolean;
  allowDecimal?: boolean;
  hardTheme?: ThemeType;
};

const InputWrapper = styled("label")<{
  themeStyle: ThemeStyle;
  error?: boolean;
}>(
  ({ themeStyle, error }) => `
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  font-weight: 400;
  line-height: 1.5;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid ${error ? Colors.RED3 : Colors.GRAY2};
  box-sizing: border-box;
  color: ${themeStyle.PrimaryText?.color ?? Colors.BLACK};
  cursor: text;
  &:focus-within {
    border-color: ${
      error ? Colors.RED3 : themeStyle.FocusedTextFieldBorder.borderColor
    };
  }

  &:has(input:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
);

const ErrorMessage = styled("div")`
  color: ${Colors.RED3};
  font-size: 9px;
  margin-top: 4px;
`;

const StyledInput = styled("input")`
  flex: 1 1 0;
  min-width: 0;
  border: none;
  background: transparent;
  text-align: right;
  padding: 0;
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;

  &:focus {
    outline: none;
  }
`;

const PrefixText = styled("span")<{ themeStyle: ThemeStyle }>`
  flex-shrink: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ themeStyle }) => themeStyle.PrimaryText?.color ?? Colors.BLACK};
`;

export default function NumberInput(props: NumberInputProps) {
  const {
    min,
    max,
    value,
    onChange,
    disabled,
    containerStyles,
    prefix,
    isError,
    altErrorMessage,
    suppressErrorMessage,
    allowDecimal,
    hardTheme,
  } = props;
  const { theme } = useTheme(hardTheme);
  const [error, setError] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      onChange(undefined);
      setError(false);
      return;
    }

    const numValue = Number(inputValue);
    const isInteger = Number.isInteger(numValue);
    const isOutOfBounds = numValue < min || numValue > max;
    const hasDecimalError = !isInteger && !allowDecimal;

    setError(isOutOfBounds || hasDecimalError);
    onChange(numValue);
  };

  return (
    <div>
      <InputWrapper
        style={containerStyles}
        themeStyle={theme}
        error={error || isError}
      >
        {prefix ? <PrefixText themeStyle={theme}>{prefix}</PrefixText> : null}
        <StyledInput
          type="number"
          inputMode={allowDecimal ? "decimal" : "numeric"}
          min={min}
          max={max}
          step={allowDecimal ? "any" : 1}
          value={value ?? ""}
          onChange={handleChange}
          disabled={disabled}
          style={{ color: theme.PrimaryText.color }}
        />
      </InputWrapper>
      {!suppressErrorMessage ? (
        <>
          {error && altErrorMessage !== undefined ? (
            <ErrorMessage style={{ textAlign: "right" }}>
              {altErrorMessage}
            </ErrorMessage>
          ) : (
            error && (
              <ErrorMessage>
                Must be {allowDecimal ? "number" : "integer"} {min}-{max}
              </ErrorMessage>
            )
          )}
        </>
      ) : null}
    </div>
  );
}
